import { Ref, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { AxiosError } from 'axios';
import { Card } from '../../../../card/Card';
import { useQuery } from '../../../../request/hooks/useQuery';
import { ObjectsConfigAPI } from '../../../api/ObjectsConfigAPI';
import { EntityUIState, MutationState } from '../../../../request/interfaces/entity-ui-state';
import { GenericPageShimmer } from '../../../../shimmer/GenericPageShimmer/GenericPageShimmer';
import { RetryApiAlert } from '../../../../shared/components/RetryApiAlert/RetryApiAlert';
import { getErrorMessageFromAxiosError } from '../../../../request/utils';
import { Icon } from '../../../../icon/Icon';
import { FlatFileObjectConfigForm } from './FlatFileObjectConfigForm/FlatFileObjectConfigForm';
import { useMutation } from '../../../../request/hooks/useMutation';
import { Integration } from '../../../../shared/interfaces/integration';
import { FLAT_FILE_OBJECT_CONFIG_DATA_IDS } from './dataIds';
import { SourceAPI } from '../../../../sources/api/SourceAPI';
import {
  DUMMY_OBJECT_ID,
  FlatFileObjectConfigFormFactory
} from './factories/flatFileObjectConfigFormFactory';
import { TemplateFormState } from '../../../../template-form/interfaces/template-form';
import styles from './FlatFileObjectsConfig.module.scss';
import {
  ObjectsConfigImperativeRefState,
  ObjectsConfigInnerState
} from '../interfaces/object-config';

interface FlatFileObjectConfigProps {
  onSave: VoidFunction;
  pipeline: Integration;
  formImperativeRef?: Ref<ObjectsConfigImperativeRefState>;
  onStateUpdate?: (state: ObjectsConfigInnerState) => void;
}

export function FlatFileObjectConfig({
  onSave,
  pipeline,
  formImperativeRef,
  onStateUpdate
}: FlatFileObjectConfigProps) {
  const [objects, setObjects] = useState<{
    [key: string]: object;
  }>({});

  const {
    data: formTemplate,
    uiState,
    fetch: fetchTemplate,
    error
  } = useQuery({
    dataSource$: () =>
      SourceAPI.getSourceObjectConfigFormTemplate(pipeline.sourceId!).then(res => {
        const { sourceObjectsFormValuesMap, ...template } = FlatFileObjectConfigFormFactory(
          res.data
        );

        setObjects(sourceObjectsFormValuesMap);

        return template;
      })
  });

  useEffect(() => {
    fetchTemplate();
  }, []);

  const { mutationState, mutate: saveChanges } = useMutation({
    mutationTarget$: () => {
      return ObjectsConfigAPI.saveObjectSelection(
        {
          all_select: true,
          search_key: ''
        },
        pipeline.id
      );
    },
    onMutationSuccess: () => onSave()
  });

  const objectsConfigIncomplete = useMemo(() => {
    return Object.keys(objects).some(objectId => objectId === DUMMY_OBJECT_ID);
  }, [objects]);

  useImperativeHandle(formImperativeRef, () => ({
    submitForm: saveChanges
  }));

  useEffect(() => {
    if (onStateUpdate) {
      onStateUpdate({
        disabled: objectsConfigIncomplete || mutationState === MutationState.MUTATING,
        savingForm: mutationState === MutationState.MUTATING
      });
    }
  }, [objectsConfigIncomplete, mutationState]);

  const dataIds = FLAT_FILE_OBJECT_CONFIG_DATA_IDS;

  return (
    <div className='flex-col h-100 overflow-hidden'>
      <div className='body-container w-100 h-100 overflow-auto'>
        {uiState === EntityUIState.LOADING ? (
          <GenericPageShimmer className='w-100' dataId={dataIds.shimmer} />
        ) : null}

        {uiState === EntityUIState.ERRORED ? (
          <div className='w-100 h-100 center-flex-row justify-center'>
            <RetryApiAlert
              title='Failed to load the form template'
              subTitle={getErrorMessageFromAxiosError(error as AxiosError)}
              secondaryCTAProps={{ onClick: fetchTemplate }}
              dataId={dataIds.retryAlert}
            />
          </div>
        ) : null}

        {uiState === EntityUIState.IDLE && !!formTemplate ? (
          <Card className={styles.flatFileCardContainer}>
            <div className='center-flex-row mb-8'>
              <Icon name='objects' className='mr-2' dataId={dataIds.objectIcon} />

              <span className='text-subheading-3 text-medium'>Object Configuration</span>
            </div>

            {Object.keys(objects).map(key => (
              <FlatFileObjectConfigForm
                key={key}
                form={{
                  ...formTemplate,
                  initialValues: {
                    ...objects[key]
                  } as unknown as TemplateFormState
                }}
                pipeline={pipeline}
                objectId={key !== DUMMY_OBJECT_ID ? key : undefined}
                onInferSchemaSuccess={(objectId, formValues) => {
                  const newObjects = {
                    ...objects,
                    [objectId]: { ...(formValues as unknown as object) }
                  };

                  if (key === DUMMY_OBJECT_ID) {
                    delete newObjects[DUMMY_OBJECT_ID];
                  }

                  setObjects(newObjects);
                }}
              />
            ))}
          </Card>
        ) : null}
      </div>
    </div>
  );
}
